import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { COLOR, FUENTE } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_OPERADORES } from './mockDataEjemplos';
import { conversionEquipo } from './calculosReportes';

const iniciales = (nombre) => (nombre || '').split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

/**
 * Desempeño por operador — detecta desvíos contra el promedio del equipo, no arma
 * ranking ni podio. Se descarta la métrica "reprogramados": id_operador solo se
 * setea al pasar a EN_CURSO/FINALIZADO, reprogramar ocurre antes y no está ligado a
 * ningún operador en el modelo de datos.
 * @param {{data: Array<{idOperador: number|null, operador: string, totalAtendidos: number,
 *   tiempoPromedioMinutos: number|null, conTramite: number, tasaConversion: number}>}} props
 */
const TablaOperadores = ({ data, compacto = false }) => {
    const filas = useMemo(() => data || [], [data]);
    const conId = useMemo(() => filas.filter((f) => f.idOperador != null), [filas]);

    const promedios = useMemo(() => {
        const prom = (campo) => {
            const valores = conId.map((f) => Number(f[campo])).filter((v) => !Number.isNaN(v) && v != null);
            return valores.length ? valores.reduce((a, b) => a + b, 0) / valores.length : null;
        };
        return {
            totalAtendidos: prom('totalAtendidos'),
            tiempoPromedioMinutos: prom('tiempoPromedioMinutos'),
            // Ponderada (trámites ÷ turnos sobre los que se calculó), no el promedio de los % de cada
            // operador: es el mismo criterio de la conversión global del encabezado.
            tasaConversion: conversionEquipo(filas),
        };
    }, [conId, filas]);

    const maxTurnos = Math.max(1, ...filas.map((f) => f.totalAtendidos || 0));
    const maxTiempo = Math.max(1, ...filas.map((f) => Number(f.tiempoPromedioMinutos) || 0));
    const maxConversion = Math.max(1, ...filas.map((f) => Number(f.tasaConversion) || 0));

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Desempeño por operador"
                        queMuestra="Te dice si cada operador está por encima o por debajo del promedio del equipo en tres cosas a la vez (cuánto atiende, cuánto tarda, cuánto convierte) — para detectar quién necesita apoyo, no para armar un ranking."
                        ejemploVisual={<TablaOperadores compacto data={MOCK_OPERADORES} />}
                        comoLeerlo={[
                            'Cada fila es un operador; las tres columnas son atenciones finalizadas (solo turnos FINALIZADO), duración media de atención y % de conversión a trámite. El período de esta pestaña es propio (hoy, 7 días o mes en curso) y no cambia con el de arriba.',
                            'El número grande es el valor del operador; el texto chico entre paréntesis es la diferencia contra el promedio del equipo. El promedio de conversión del equipo es ponderado (trámites abiertos ÷ turnos sobre los que se calculó), como la conversión del encabezado; la conversión de cada operador se calcula sin cobros ni quienes vinieron por un trámite que ya tenían, por eso su base es menor que sus atenciones finalizadas.',
                            'Verde = mejor que el promedio en esa métrica. Rojo = peor. Para "duración media", menos es mejor (verde); para las otras dos, más es mejor.',
                            'La barrita debajo de cada número es una escala visual: la franja de color es el valor del operador, la línea negra vertical marca dónde está el promedio del equipo.',
                        ]}
                        analisis="Rosario atiende más turnos que nadie (45, verde) pero es la más lenta (32 min, rojo) y la que menos convierte (4.4%, rojo) — el volumen alto no se está traduciendo en resultado. Facu es el caso inverso: atiende menos turnos (25, rojo) pero es la más rápida (14 min, verde) y la que más convierte (12.0%, verde). Mirando solo 'turnos atendidos' se podría pensar que Rosario rinde más y Facu menos — las otras dos columnas dicen justo lo contrario."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Desempeño por operador</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '16px' }}>
                        Comparado contra el promedio del equipo — el objetivo es detectar desvíos, no competir.
                    </Typography>
                </>
            )}
            {!compacto && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', paddingBottom: '8px' }}>
                    <Box sx={{ width: 34, flexShrink: 0 }} />
                    <Box sx={{ width: 140, flexShrink: 0 }} />
                    {['Atenciones finalizadas', 'Duración media', 'Conversión a trámite'].map((titulo) => (
                        <Typography key={titulo} sx={{
                            flex: 1, fontFamily: FUENTE, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '0.05em', color: COLOR.grisTexto,
                        }}>
                            {titulo}
                        </Typography>
                    ))}
                </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: compacto ? '8px' : '12px' }}>
                {filas.map((f) => (
                    <Box key={f.idOperador ?? 'sin-operador'} sx={{ display: 'flex', alignItems: 'center', gap: compacto ? '8px' : '14px', paddingBottom: compacto ? '6px' : '10px', borderBottom: '1px solid #F1F5F9' }}>
                        {!compacto && (
                            <Box sx={{
                                width: 34, height: 34, borderRadius: '50%', background: COLOR.gris, color: COLOR.negro,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                fontFamily: FUENTE, fontSize: 12, fontWeight: 700,
                            }}>
                                {f.idOperador != null ? iniciales(f.operador) : '—'}
                            </Box>
                        )}
                        <Typography sx={{ fontFamily: FUENTE, fontSize: compacto ? 11.5 : 13, color: COLOR.negro, width: compacto ? 60 : 140, flexShrink: 0 }} noWrap title={f.operador}>
                            {f.operador}
                        </Typography>
                        <Metrica valor={f.totalAtendidos} promedio={promedios.totalAtendidos} max={maxTurnos} sufijo="" masEsMejor compacto={compacto} />
                        <Metrica valor={f.tiempoPromedioMinutos != null ? Number(f.tiempoPromedioMinutos) : null} promedio={promedios.tiempoPromedioMinutos} max={maxTiempo} sufijo=" min" masEsMejor={false} compacto={compacto} />
                        <Metrica valor={f.tasaConversion != null ? Number(f.tasaConversion) : null} promedio={promedios.tasaConversion} max={maxConversion} sufijo="%" masEsMejor compacto={compacto} />
                    </Box>
                ))}
            </Box>
        </Card>
    );
};

const Metrica = ({ valor, promedio, max, sufijo, masEsMejor, compacto = false }) => {
    if (valor == null) {
        return <Box sx={{ flex: 1 }}><Typography sx={{ fontFamily: FUENTE, fontSize: 12, color: COLOR.grisTexto }}>—</Typography></Box>;
    }
    const diferencia = promedio != null ? valor - promedio : null;
    const mejor = diferencia != null && (masEsMejor ? diferencia > 0 : diferencia < 0);
    const peor = diferencia != null && (masEsMejor ? diferencia < 0 : diferencia > 0);
    const color = mejor ? COLOR.verde : peor ? COLOR.rojo : COLOR.negro;
    const posicionValor = Math.min(100, (valor / max) * 100);
    const posicionPromedio = promedio != null ? Math.min(100, (promedio / max) * 100) : null;

    return (
        <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: FUENTE, fontSize: compacto ? 11.5 : 13, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
                {valor.toFixed(sufijo === '%' || sufijo === ' min' ? 1 : 0)}{sufijo}
                {!compacto && diferencia != null && (
                    <span style={{ fontSize: 11, fontWeight: 500, color: COLOR.grisTexto, marginLeft: 6 }}>
                        ({diferencia > 0 ? '+' : ''}{diferencia.toFixed(1)} vs. equipo)
                    </span>
                )}
            </Typography>
            <Box sx={{ position: 'relative', height: 6, borderRadius: 3, background: COLOR.gris, marginTop: '4px' }}>
                <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${posicionValor}%`, borderRadius: 3, background: color }} />
                {posicionPromedio != null && (
                    <Box sx={{ position: 'absolute', top: -2, bottom: -2, left: `${posicionPromedio}%`, width: 2, background: COLOR.negro }} />
                )}
            </Box>
        </Box>
    );
};

export default TablaOperadores;
